"use client"

/**
 * Client-side event data facade.
 *
 * - Sync methods delegate to the browser timeline store (mutations, localStorage, in-memory
 *   ALL_EVENTS). This preserves current SPA behavior.
 * - Async `fetch*` helpers call Route Handlers under `/api/events`, which use
 *   `server/services/events.service` (mock seed today → DB later).
 *
 * Do not import `@/server/*` from UI or hooks — use these fetch helpers or keep sync store access.
 */

import {
  isApiFailureBody,
  isApiSuccessBody,
  readApiErrorMessage,
} from "@/lib/api/api-envelope"
import { throwIfPlanEntitlementBlocked } from "@/lib/billing/plan-entitlement-blocked-error"
import { deleteMedia } from "@/lib/api/media"
import type {
  ApiEventDto,
  ApiEventListItemDto,
} from "@/lib/api/events-contract"
import {
  sortUserEventListItems,
  type UserEventListItem,
} from "@/features/events/user-event-collection"
import {
  EVENT_MEDIA_PURPOSE_ALBUM,
  EVENT_MEDIA_PURPOSE_COVER,
} from "@/lib/events/event-media-purpose"
import type {
  ApiEventMediaDto,
  CreateEventInput,
  MyRelatedEventListDto,
  PatchEventBody,
} from "@/lib/events/schemas"
import { normalizeCreateEventApiInputForZod } from "@/lib/events/normalize-create-event-api-input"
import type { EventData, EventMediaItem } from "@/lib/events/types"
import { normalizeImageMediaDimensionsFromMetadata } from "@/lib/media/normalize-media-dimensions"
import {
  ALL_EVENTS,
  getEventById as getEventByIdFromStore,
  getUserCreatedEvents as getUserCreatedEventsFromStore,
} from "@/lib/timeline-store"

export type { EventData, RsvpStatus, TimelineSave, CreateEventData } from "@/lib/timeline-store"

/** Full event catalog in the browser (includes client-side mutations). */
export function getEvents(): EventData[] {
  return ALL_EVENTS
}

export function getEventById(id: string): EventData | undefined {
  return getEventByIdFromStore(id)
}

export function getUserCreatedEvents(): EventData[] {
  return getUserCreatedEventsFromStore()
}

export function resolveStoredEventById(id: string): EventData | undefined {
  return (
    getEventByIdFromStore(id) ??
    getUserCreatedEventsFromStore().find((e) => e.id === id)
  )
}

/**
 * Merge local event state with a fresh API snapshot. `media` always follows the
 * server payload so stale local or placeholder album rows never override real
 * backend gallery data after a successful fetch.
 */
export function mergeEventFromApiResponse(
  local: EventData,
  api: EventData,
): EventData {
  return {
    ...local,
    ...api,
    media: Array.isArray(api.media) ? api.media : [],
  }
}

/** Maps nested media DTOs from the API to `EventData.media` dates. */
export function apiMediaDtosToEventMedia(
  items: ApiEventMediaDto[] | undefined,
): EventMediaItem[] {
  if (!items?.length) return []
  return items.map((m) => {
    const dim =
      m.type === "image"
        ? normalizeImageMediaDimensionsFromMetadata(m.metadata)
        : null
    return {
      id: m.id,
      eventId: m.eventId,
      uploadedByUserId: m.userId?.trim() || m.uploadedByUserId,
      userId: m.userId?.trim() || m.uploadedByUserId,
      eventOwnerId: m.eventOwnerId?.trim() || undefined,
      ...(typeof m.canDelete === "boolean" ? { canDelete: m.canDelete } : {}),
      type: m.type,
      purpose:
        m.purpose === EVENT_MEDIA_PURPOSE_COVER
          ? EVENT_MEDIA_PURPOSE_COVER
          : EVENT_MEDIA_PURPOSE_ALBUM,
      cfImageId: m.cfImageId,
      externalUrl: m.externalUrl,
      streamUid: m.streamUid,
      metadata: m.metadata,
      status: m.status ?? undefined,
      durationSeconds: m.durationSeconds ?? undefined,
      thumbnailUrl: m.thumbnailUrl ?? undefined,
      previewUrl: m.previewUrl ?? undefined,
      playbackUrl: m.playbackUrl ?? undefined,
      hlsUrl: m.hlsUrl ?? undefined,
      imagePixelWidth: m.imagePixelWidth ?? dim?.width,
      imagePixelHeight: m.imagePixelHeight ?? dim?.height,
      ...(m.type === "image" && m.isHeroCandidate === true
        ? { isHeroCandidate: true as const }
        : {}),
      createdAt: new Date(m.createdAt),
    }
  })
}

/** Maps a wire DTO to client `EventData` (e.g. after POST /api/events). */
export function apiEventDtoToEventData(dto: ApiEventDto): EventData {
  return {
    id: dto.id,
    title: dto.title,
    date: dto.date,
    time: dto.time,
    eventTimeZone: dto.eventTimeZone,
    startDateLocal: dto.startDateLocal,
    startTimeLocal: dto.startTimeLocal,
    startAtUtc: dto.startAtUtc,
    seriesId: dto.seriesId ?? undefined,
    location: dto.location,
    locationName: dto.locationName,
    address: dto.address,
    lat: dto.lat,
    lng: dto.lng,
    image: dto.image,
    imageThumbnailUrl: dto.imageThumbnailUrl,
    hasPersistedShellCover: dto.hasPersistedShellCover,
    description: dto.description,
    hostName: dto.hostName,
    hostAvatar: dto.hostAvatar,
    attendees: dto.attendees,
    isPublished: dto.isPublished,
    hostArchivedAt: dto.hostArchivedAt ?? undefined,
    visibility: dto.visibility,
    allowGuestPhotoUploads: dto.allowGuestPhotoUploads,
    allowGuestVideoUploads: dto.allowGuestVideoUploads,
    allowSignedInInviteesToViewAlbum: dto.allowSignedInInviteesToViewAlbum,
    requireSignInToViewEventAlbum: dto.requireSignInToViewEventAlbum,
    canViewEventMedia: dto.canViewEventMedia,
    publicAppearanceDark: dto.publicAppearanceDark,
    links: dto.links,
    contributions: dto.contributions,
    createdAt: new Date(dto.createdAt),
    createdByUserId: dto.createdByUserId,
    isWorkspaceEventHost: dto.isWorkspaceEventHost,
    eventManagementRole: dto.eventManagementRole,
    videoUid: dto.videoUid ?? undefined,
    videoStatus: dto.videoStatus ?? undefined,
    videoSignedPlayback: dto.videoSignedPlayback,
    videoEmbedUrl: dto.videoEmbedUrl,
    videoHlsUrl: dto.videoHlsUrl,
    videoThumbnailUrl: dto.videoThumbnailUrl,
    media: apiMediaDtosToEventMedia(
      Array.isArray(dto.media) ? dto.media : [],
    ),
    canUploadMedia: dto.canUploadMedia,
    canUploadPhotos: dto.canUploadPhotos,
    canUploadVideos: dto.canUploadVideos,
    isSavedByUser: dto.isSavedByUser,
    attendeeCount: dto.attendeeCount,
    isAttending: dto.isAttending,
    attendanceRole:
      dto.attendanceRole === undefined ? undefined : dto.attendanceRole,
    isRsvped: dto.isRsvped,
    rsvpStatus: dto.rsvpStatus === undefined ? undefined : dto.rsvpStatus,
    participantRoster: dto.participantRoster?.map((p) => ({
      userId: p.userId,
      displayName: p.displayName,
      avatarUrl: p.avatarUrl ?? null,
    })),
    contributionsReopenUntil: dto.contributionsReopenUntil ?? undefined,
    memoryCollection: dto.memoryCollection,
    shareAccessToken: dto.shareAccessToken ?? undefined,
    shareLinkDisabled: dto.shareLinkDisabled,
  }
}

/** Maps list/catalog wire rows (no album array) to client `EventData`. */
export function apiEventListItemDtoToEventData(
  dto: ApiEventListItemDto,
): EventData {
  const { mediaCount, ...rest } = dto
  const fullDto: ApiEventDto = { ...rest, media: [] }
  const fromFull = apiEventDtoToEventData(fullDto)
  return {
    ...fromFull,
    media: [],
    mediaCount,
  }
}

/** Create event on the server; caller should {@link registerServerCreatedEvent} for local compatibility. */
export async function createEventViaApi(
  input: CreateEventInput,
): Promise<ApiEventDto> {
  const payload = normalizeCreateEventApiInputForZod(input)
  const res = await fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throwIfPlanEntitlementBlocked(body)
    throw new Error(
      readApiErrorMessage(body, `POST /api/events failed: ${res.status}`),
    )
  }
  if (!isApiSuccessBody<ApiEventDto>(body)) {
    throw new Error(`POST /api/events failed: malformed response`)
  }
  return body.data
}

/** No matching persisted row for DELETE /api/events/[id] (client may fall back to local-only store). */
export class EventDeleteNotOnServerError extends Error {
  override readonly name = "EventDeleteNotOnServerError"
  constructor() {
    super("Event is not stored on the server")
  }
}

export type DeleteEventApiScope = "default" | "draft" | "series"

/**
 * Delete event rows on the server.
 * Throws {@link EventDeleteNotOnServerError} when the API responds 404.
 */
export async function deleteEventViaApi(
  eventId: string,
  opts?: { scope?: DeleteEventApiScope },
): Promise<void> {
  const eid = eventId.trim()
  const scope = opts?.scope ?? "default"
  const base = `/api/events/${encodeURIComponent(eid)}`
  const path =
    scope === "default"
      ? base
      : `${base}?scope=${encodeURIComponent(scope)}`
  const res = await fetch(path, {
    method: "DELETE",
  })
  const text = await res.text()
  let body: unknown = null
  if (text.trim()) {
    try {
      body = JSON.parse(text) as unknown
    } catch {
      body = null
    }
  }
  if (res.status === 404) {
    throw new EventDeleteNotOnServerError()
  }
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(
        body,
        `DELETE /api/events/${eid} failed: ${res.status}`,
      ),
    )
  }
  if (!isApiSuccessBody<{ success: true }>(body)) {
    throw new Error(`DELETE /api/events/${eid} failed: malformed response`)
  }
}

/** Host-only partial update via PATCH /api/events/[id]. */
export async function patchEventViaApi(
  eventId: string,
  patch: PatchEventBody,
): Promise<ApiEventDto> {
  const res = await fetch(`/api/events/${encodeURIComponent(eventId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throwIfPlanEntitlementBlocked(body)
    throw new Error(
      readApiErrorMessage(
        body,
        `PATCH /api/events/${eventId} failed: ${res.status}`,
      ),
    )
  }
  if (!isApiSuccessBody<ApiEventDto>(body)) {
    throw new Error(`PATCH /api/events/${eventId} failed: malformed response`)
  }
  return body.data
}

/** Remove a gallery `Media` row; server enforces uploader or event owner. */
export async function deleteEventMediaViaApi(mediaId: string): Promise<void> {
  await deleteMedia(mediaId)
}

/** Server-shaped list (mock seed); does not reflect unsynced client mutations. */
export async function fetchEventsFromApi(): Promise<ApiEventListItemDto[]> {
  const res = await fetch("/api/events", { cache: "no-store" })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(body, `GET /api/events failed: ${res.status}`),
    )
  }
  if (!isApiSuccessBody<ApiEventListItemDto[]>(body)) {
    throw new Error(`GET /api/events failed: malformed response`)
  }
  return body.data
}

/** Coalesces concurrent GETs (Strict Mode, header + page, etc.). */
const inflightEventByKey = new Map<string, Promise<ApiEventDto | null>>()

function eventFetchKey(id: string, shareToken: string): string {
  return `${id.trim()}\u0000${shareToken}`
}

/** Server-shaped row (mock seed); does not reflect unsynced client mutations. */
export async function fetchEventFromApi(
  id: string,
  opts?: { shareToken?: string | null },
): Promise<ApiEventDto | null> {
  const eid = id.trim()
  if (!eid) return null
  const token = opts?.shareToken?.trim() ?? ""
  const key = eventFetchKey(eid, token)
  const existing = inflightEventByKey.get(key)
  if (existing) return existing

  const run = async (): Promise<ApiEventDto | null> => {
    const q = token ? `?share=${encodeURIComponent(token)}` : ""
    const res = await fetch(`/api/events/${encodeURIComponent(eid)}${q}`, {
      cache: "no-store",
    })
    const body: unknown = await res.json()
    if (res.status === 404) {
      return null
    }
    if (!res.ok || isApiFailureBody(body)) {
      throw new Error(
        readApiErrorMessage(
          body,
          `GET /api/events/${eid} failed: ${res.status}`,
        ),
      )
    }
    if (!isApiSuccessBody<ApiEventDto>(body)) {
      throw new Error(`GET /api/events/${eid} failed: malformed response`)
    }
    return body.data
  }

  const p = run().finally(() => {
    inflightEventByKey.delete(key)
  })
  inflightEventByKey.set(key, p)
  return p
}

export type StreamMediaSyncApiResult = {
  candidates: number
  transitioned: number
}

function isStreamMediaSyncPayload(
  v: unknown,
): v is StreamMediaSyncApiResult {
  if (v == null || typeof v !== "object") return false
  const o = v as Record<string, unknown>
  return (
    typeof o.candidates === "number" &&
    typeof o.transitioned === "number"
  )
}

/** Best-effort Cloudflare Stream status refresh (no throw); use while videos are processing. */
export async function postEventStreamMediaSyncFromApi(
  eventId: string,
  opts?: { shareToken?: string | null },
): Promise<StreamMediaSyncApiResult | null> {
  const id = eventId.trim()
  if (!id) return null
  const token = opts?.shareToken?.trim()
  const q = token ? `?share=${encodeURIComponent(token)}` : ""
  try {
    const res = await fetch(
      `/api/events/${encodeURIComponent(id)}/stream-media-sync${q}`,
      { method: "POST", credentials: "include" },
    )
    if (!res.ok) return null
    const body: unknown = await res.json()
    if (!isApiSuccessBody(body) || !isStreamMediaSyncPayload(body.data)) {
      return null
    }
    return body.data
  } catch {
    return null
  }
}

/** Events the signed-in user attends as a guest (not hosting). */
export async function fetchParticipatingEventsFromApi(): Promise<
  ApiEventListItemDto[]
> {
  const res = await fetch("/api/events/participating", { cache: "no-store" })
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    throw new Error(
      readApiErrorMessage(
        body,
        `GET /api/events/participating failed: ${res.status}`,
      ),
    )
  }
  if (!isApiSuccessBody<ApiEventListItemDto[]>(body)) {
    throw new Error(`GET /api/events/participating failed: malformed response`)
  }
  return body.data
}

let inflightMyRelated: Promise<MyRelatedEventListDto[]> | null = null

export async function fetchMyRelatedEventsFromApi(): Promise<
  MyRelatedEventListDto[]
> {
  if (inflightMyRelated) return inflightMyRelated
  inflightMyRelated = (async () => {
    try {
      const rows: MyRelatedEventListDto[] = []
      let cursor: string | null = null
      do {
        const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""
        const res = await fetch(`/api/events/my${q}`, { cache: "no-store" })
        const body: unknown = await res.json()
        if (!res.ok || isApiFailureBody(body)) {
          throw new Error(
            readApiErrorMessage(
              body,
              `GET /api/events/my failed: ${res.status}`,
            ),
          )
        }
        if (!isApiSuccessBody<MyRelatedEventListDto[]>(body)) {
          throw new Error(`GET /api/events/my failed: malformed response`)
        }
        rows.push(...body.data)
        const next = body.meta?.nextCursor
        cursor = typeof next === "string" && next.trim() ? next : null
      } while (cursor != null)
      return rows
    } finally {
      inflightMyRelated = null
    }
  })()
  return inflightMyRelated
}

export function apiMyRelatedDtoToUserEventListItem(
  dto: MyRelatedEventListDto,
): UserEventListItem {
  return {
    ...apiEventListItemDtoToEventData(dto),
    isHosted: dto.isHosted,
    isAttending: dto.isAttending,
    isSaved: dto.isSaved,
    isRsvped: dto.isRsvped,
    rsvpStatus: dto.rsvpStatus,
    primaryRelationship: dto.primaryRelationship,
  }
}

/** Maps `GET /api/events/my` DTOs to sorted list items (no local catalog merge). */
export function userEventListItemsFromMyRelatedDtos(
  serverDtos: MyRelatedEventListDto[],
): UserEventListItem[] {
  return sortUserEventListItems(serverDtos.map(apiMyRelatedDtoToUserEventListItem))
}
